import * as AuthSession from "expo-auth-session";
import Constants from "expo-constants";

import { getConfig } from "@/getConfig";
import { getFeathersClient } from "@/lib/feathers/client";
import { log } from "@/logger";
import type { AuthenticationResult } from "@spork/feathers-client";

const MICROSOFT_DISCOVERY_URL = "https://login.microsoftonline.com/common/v2.0";

function getMicrosoftClientId(): string {
    return getConfig("microsoftClientId") ?? "";
}

function getAuthScheme(): string {
    return (
        (Constants.expoConfig?.scheme as string | undefined) ??
        (process.env.APP_VARIANT === "prod"
            ? "spork-parent"
            : `spork-parent-${process.env.APP_VARIANT?.toLowerCase()}`)
    );
}

export async function signInWithMicrosoft(): Promise<AuthenticationResult> {
    log.debug("Signing in with Microsoft");
    const microsoftClientId = getMicrosoftClientId();
    if (!microsoftClientId) {
        throw new Error("not-configured");
    }

    const discovery = await AuthSession.resolveDiscoveryAsync(
        MICROSOFT_DISCOVERY_URL,
    );

    const requestCommon = {
        clientId: microsoftClientId,
        redirectUri: AuthSession.makeRedirectUri({
            scheme: getAuthScheme(),
            path: "login",
        }),
    };

    const authRequest = new AuthSession.AuthRequest({
        ...requestCommon,
        scopes: ["openid", "profile", "email", "User.Read"],
        prompt: AuthSession.Prompt.SelectAccount,
    });

    const authorizeResult = await authRequest.promptAsync(discovery);
    log.debug("Authorize result", authorizeResult);

    if (
        authorizeResult.type === "cancel" ||
        authorizeResult.type === "dismiss"
    ) {
        throw new Error("canceled");
    }

    if (authorizeResult.type !== "success" || !authRequest.codeVerifier) {
        throw new Error("not-completed");
    }

    const tokenResult = await AuthSession.exchangeCodeAsync(
        {
            ...requestCommon,
            code: authorizeResult.params.code,
            extraParams: {
                code_verifier: authRequest.codeVerifier,
            },
        },
        discovery,
    );

    const idToken = tokenResult.idToken;
    if (!idToken) {
        throw new Error("no-id-token");
    }

    const client = getFeathersClient();
    log.debug(
        "Authenticating Feathers client with Microsoft ID token:",
        idToken,
    );
    return client.authenticate({
        strategy: "microsoft",
        access_token: idToken,
    });
}
